/**
 * Hell, dunkel oder wie das Betriebssystem.
 *
 * Gesetzt wird ein Attribut am Wurzelelement (`data-farbschema="dunkel"`).
 * Daran haengt in `index.css` ein Block, der die Farbvariablen umdefiniert -
 * deshalb gibt es in den Bausteinen kein einziges `dark:`.
 *
 * Die Wahl liegt im `localStorage`, nicht im Benutzerkonto: Sie gehoert zum
 * Geraet, nicht zur Person. Wer am Arbeitsplatz hell und auf dem Telefon im
 * Einsatz dunkel arbeiten will, soll das koennen.
 */

export type Farbschema = 'system' | 'hell' | 'dunkel';

export const FARBSCHEMATA: ReadonlyArray<{ id: Farbschema; label: string }> = [
  { id: 'system', label: 'System' },
  { id: 'hell', label: 'Hell' },
  { id: 'dunkel', label: 'Dunkel' },
];

const SCHLUESSEL = 'farbschema';

const istFarbschema = (wert: unknown): wert is Farbschema =>
  wert === 'system' || wert === 'hell' || wert === 'dunkel';

/** Was gespeichert ist - `system`, wenn nichts oder etwas Unbekanntes dasteht. */
export const gespeichertesFarbschema = (): Farbschema => {
  try {
    const wert = localStorage.getItem(SCHLUESSEL);
    return istFarbschema(wert) ? wert : 'system';
  } catch {
    // Privates Fenster mit gesperrtem Speicher: kein Grund, die Anwendung
    // daran scheitern zu lassen.
    return 'system';
  }
};

/** Was das Betriebssystem sagt. */
export const systemMoechteDunkel = (): boolean => (
  typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-color-scheme: dark)').matches
);

/** Was daraus tatsaechlich folgt. */
export const istDunkel = (schema: Farbschema): boolean => (
  schema === 'dunkel' || (schema === 'system' && systemMoechteDunkel())
);

/**
 * Traegt das Ergebnis an das Wurzelelement.
 *
 * Im hellen Fall wird das Attribut **entfernt** statt auf "hell" gesetzt: So
 * gibt es genau einen Zustand, an dem der CSS-Block haengt, und nicht zwei,
 * die auseinanderlaufen koennen.
 */
export const anwenden = (schema: Farbschema): void => {
  if (typeof document === 'undefined') return;
  const wurzel = document.documentElement;
  if (istDunkel(schema)) wurzel.setAttribute('data-farbschema', 'dunkel');
  else wurzel.removeAttribute('data-farbschema');
};

export const speichern = (schema: Farbschema): void => {
  try {
    if (schema === 'system') localStorage.removeItem(SCHLUESSEL);
    else localStorage.setItem(SCHLUESSEL, schema);
  } catch {
    // Siehe oben - die Wahl gilt dann nur fuer diese Sitzung.
  }
};
